import clsx from 'clsx';
import { X } from 'lucide-react';
import { useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { MailRecipientOption, UserRef } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * Whom a mail goes to: the people picked, as chips, and a box to type a name
 * into. Typing narrows the company's people — by name in either language, job
 * title or department — and Enter, or a click, adds the one highlighted. The
 * arrow keys move through the matches; Backspace in an empty box takes off the
 * last person added.
 *
 * The list opens on typing, on ↓, or on a click in the box, and closes once
 * someone is added: left open, it lies over the fields below, and a click meant
 * for them would add whoever it covered.
 *
 * It sits inside the compose page's form, so Enter never submits it: sending
 * is the Send button's alone.
 */

/** As many matches as fit without scrolling far. */
const SHOWN = 8;
/** The list's tallest; with less room it shrinks, never below a few rows. */
const LIST_MAX = 288;
const LIST_MIN = 120;
const GAP = 8;

interface Placement {
  above: boolean;
  maxHeight: number;
}

/**
 * Where the list fits: between the box and what stays on screen over the page —
 * the app's top bar above it, and below it a form's sticky Save bar, or the
 * window's edge. The bar sits right under a short form, not at the window's
 * edge, so measuring to the edge alone opened the list under it. It opens on the
 * side with room for all of it, else the roomier one, as tall as fits there.
 */
function placeList(box: HTMLElement): Placement {
  const rect = box.getBoundingClientRect();
  const actions = box.closest('form')?.querySelector('[data-form-actions]');
  const floor = Math.min(window.innerHeight, actions?.getBoundingClientRect().top ?? Infinity);
  const ceiling = Math.max(0, document.querySelector('[data-top-bar]')?.getBoundingClientRect().bottom ?? 0);
  const below = floor - rect.bottom - GAP;
  const above = rect.top - ceiling - GAP;
  const up = below < LIST_MAX && above > below;
  return { above: up, maxHeight: Math.max(LIST_MIN, Math.min(LIST_MAX, up ? above : below)) };
}

/**
 * For matching only: lower case, and Arabic without its marks and with its
 * letter variants folded together, so "احمد" finds "أحمد" and "اسامة" finds "أسامه".
 */
export function fold(text: string | null | undefined): string {
  return (text ?? '')
    .toLowerCase()
    .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
    .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627')
    .replace(/\u0649/g, '\u064A')
    .replace(/\u0629/g, '\u0647')
    .trim();
}

export function PeoplePicker({
  people,
  value,
  onChange,
  max,
  known,
  invalid = false,
  inputId,
  fullLabel,
}: {
  /** Everyone a mail can go to; undefined while loading. */
  people: MailRecipientOption[] | undefined;
  value: string[];
  onChange: (ids: string[]) => void;
  max: number;
  /** Names for people picked who are not in `people` — whoever a reply was prefilled with. */
  known?: ReadonlyMap<string, UserRef>;
  invalid?: boolean;
  inputId?: string;
  /** What the box says once `max` are picked. A mail's limit by default. */
  fullLabel?: string;
}) {
  const { t, locale, localized } = useI18n();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const listId = useId();
  // A box at the foot of a form opens its list above: below, the sticky Save bar would cover it.
  const [place, setPlace] = useState<Placement>({ above: false, maxHeight: LIST_MAX });

  const byId = useMemo(() => {
    const map = new Map<string, UserRef>(known);
    for (const p of people ?? []) map.set(p.id, p);
    return map;
  }, [people, known]);

  const matches = useMemo(() => {
    const words = fold(query).split(/\s+/).filter(Boolean);
    const taken = new Set(value);
    return (people ?? [])
      .filter((p) => !taken.has(p.id))
      .filter((p) => {
        if (words.length === 0) return true;
        const haystack = fold(
          [p.fullName, p.fullNameAr, p.jobTitle, ...p.departments.flatMap((d) => [d.name, d.nameAr])].join(' '),
        );
        return words.every((word) => haystack.includes(word));
      })
      .sort((a, b) => localized(a).localeCompare(localized(b), locale))
      .slice(0, SHOWN);
  }, [people, query, value, localized, locale]);

  const full = value.length >= max;
  const showList = open && !full && (query.trim().length > 0 || matches.length > 0);
  const highlighted = matches[Math.min(active, matches.length - 1)];

  // Placed before it shows, and again while it is open if the page scrolls or the window changes.
  useLayoutEffect(() => {
    const el = box.current;
    if (!showList || !el) return;
    const update = () => {
      const next = placeList(el);
      setPlace((was) => (was.above === next.above && was.maxHeight === next.maxHeight ? was : next));
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showList]);

  const add = (id: string) => {
    if (value.includes(id) || full) return;
    onChange([...value, id]);
    setQuery('');
    setActive(0);
    setOpen(false);
    input.current?.focus();
  };
  const remove = (id: string) => onChange(value.filter((x) => x !== id));

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // An input method finishing a word (Arabic keyboards) presses Enter too.
    if (e.nativeEvent.isComposing) return;
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        if (showList && highlighted) add(highlighted.id);
        break;
      case 'ArrowDown':
        e.preventDefault();
        // The first press opens the list on its first match; the next ones move down it.
        if (!open) {
          setOpen(true);
          setActive(0);
        } else {
          setActive((i) => Math.min(i + 1, Math.max(0, matches.length - 1)));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case 'Escape':
        if (open) {
          e.preventDefault();
          setOpen(false);
        }
        break;
      case 'Backspace':
        if (!query && value.length) remove(value[value.length - 1]);
        break;
    }
  };

  return (
    <div className="relative">
      <div
        ref={box}
        className={clsx(
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border bg-[var(--surface-raised)] px-2 py-1.5',
          // The box shows focus, not the input inside it: two rings would nest.
          'transition-colors duration-fast focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
          invalid ? 'border-warning/60' : 'hairline',
        )}
        onClick={() => {
          input.current?.focus();
          setOpen(true);
        }}
      >
        {value.map((id) => {
          const person = byId.get(id);
          const name = localized(person);
          return (
            <span
              key={id}
              className="inline-flex max-w-full items-center gap-1 rounded-pill bg-primary/12 py-0.5 pe-1 ps-2.5 text-body-sm text-[var(--text-primary)] ring-1 ring-inset ring-primary/30"
            >
              <bdi className="truncate">{name}</bdi>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(id);
                }}
                className="shrink-0 rounded-full p-0.5 text-[var(--text-tertiary)] hover:bg-white/10 hover:text-[var(--text-primary)]"
                aria-label={t('mail.toRemove', { name })}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          );
        })}
        <input
          ref={input}
          id={inputId}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && highlighted ? `${listId}-${highlighted.id}` : undefined}
          value={query}
          disabled={!people || full}
          dir="auto"
          placeholder={
            !people ? t('mail.toLoading') : full ? (fullLabel ?? t('mail.toMax', { n: max })) : value.length ? '' : t('mail.toPlaceholder')
          }
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          // Late enough that a click on a match lands first.
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          className="h-7 min-w-[8rem] flex-1 bg-transparent px-1 text-body text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] dark:placeholder:text-[var(--text-secondary)] focus:outline-none focus-visible:shadow-none disabled:cursor-not-allowed"
        />
      </div>

      {showList && (
        <ul
          id={listId}
          role="listbox"
          style={{ maxHeight: place.maxHeight }}
          className={clsx(
            'absolute inset-x-0 z-dropdown overflow-y-auto rounded-md surface py-1 shadow-lg',
            place.above ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2.5 text-body-sm text-muted">{t('mail.toNoMatch')}</li>
          ) : (
            matches.map((p, i) => {
              const where = [p.jobTitle, ...p.departments.map((d) => localized(d))].filter(Boolean).join(' · ');
              return (
                <li
                  key={p.id}
                  id={`${listId}-${p.id}`}
                  role="option"
                  aria-selected={p.id === highlighted?.id}
                  // Before the input's blur, so the list is still there for the click.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => add(p.id)}
                  onMouseEnter={() => setActive(i)}
                  className={clsx(
                    'cursor-pointer px-3 py-2',
                    p.id === highlighted?.id ? 'bg-primary/10' : 'hover:bg-[var(--surface-sunken)]',
                  )}
                >
                  <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                    <bdi>{localized(p)}</bdi>
                  </span>
                  {where && (
                    <span className="block truncate text-caption text-muted">
                      <bdi>{where}</bdi>
                    </span>
                  )}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
